/**
 * SzBranding - Global branding overrides applied at app initialization.
 * This runs once when the app loads, patching globalConfig before Vue mounts.
 * Import this in the main app entry point.
 */

const BRAND = 'SolarZ';
const THEME_COLOR = '#388E3C';
const LOGO_LIGHT = 'https://cdn.prod.website-files.com/668c4852c0375980f89dda57/668d9c8f19c56d433becc6f2_img-logo-solarz-black.png';
const LOGO_DARK = 'https://cdn.prod.website-files.com/668c4852c0375980f89dda57/66bfc57a77dc997e6a193803_img-logo-solarz-home-branco.png';
const FAVICON_DATA = 'data:image/svg+xml,' + encodeURIComponent('<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="8" fill="%23388E3C"/><path d="M20.2684 14.5391V12.0531C20.2684 11.8404 20.4476 11.668 20.6687 11.668H32.753C32.9741 11.668 33.1534 11.8404 33.1534 12.0531V14.5187C33.1534 14.5548 33.1418 14.59 33.1203 14.6194L28.5409 20.8829C28.4795 20.9668 28.3796 21.0167 28.2729 21.0167H25.2298C24.8769 21.0167 24.6697 20.635 24.8714 20.3564L28.8038 14.9243H20.6687C20.4476 14.9243 20.2684 14.7518 20.2684 14.5391Z" fill="white"/><path d="M15.8642 25.1309L21.1534 17.791C21.2146 17.706 21.3152 17.6554 21.4226 17.6554H24.6344C24.9466 17.6554 25.1387 17.9837 24.9767 18.2404L21.0145 24.5181H31.7896C31.9096 24.5181 32.0173 24.5889 32.0609 24.6965L33.3034 27.7618C33.4151 28.0373 33.2038 28.3346 32.8965 28.3346H16.3373C16.076 28.3346 15.8642 28.1308 15.8642 27.8795V25.1309Z" fill="white"/><path d="M14.5578 28.1169V24.8992C13.4127 25.1238 11.2203 25.1967 9.87656 22.93C9.74203 22.7031 9.44492 22.6063 9.20627 22.731L6.91139 23.93C6.68453 24.0485 6.59829 24.3213 6.72689 24.5366C9.0957 28.5028 12.0811 28.3364 14.5578 28.1169Z" fill="white"/><path d="M17.9261 20.1182L15.8297 23.1318C15.6727 22.7315 14.9818 21.8629 13.4742 21.591C11.9196 21.338 8.62189 20.4331 7.86813 18.8379C7.19681 17.8938 6.39829 15.4594 8.57478 13.2751C9.99791 11.8468 12.2539 11.6066 14.1456 11.7708C15.992 12.0806 17.9405 12.7633 19.2051 15.1188C19.3194 15.3317 19.2266 15.5906 19.0064 15.7024L16.6807 16.8829C16.4606 16.9946 16.1891 16.9216 16.0471 16.725C15.6255 16.1413 15.1356 15.6348 14.864 15.4617C13.4742 14.6913 11.8921 15.1407 11.4721 15.4617C11.2941 15.5977 10.8478 15.8809 10.8478 16.606C10.8478 17.177 11.6844 17.7846 12.1434 17.9542C14.5342 18.8379 16.3362 18.9172 17.9261 20.1182Z" fill="white"/></svg>');

export function initSzBranding() {
  // Patch globalConfig before Vue mounts
  if (window.globalConfig) {
    window.globalConfig.LOGO_THUMBNAIL = LOGO_LIGHT;
    window.globalConfig.BRAND_NAME = BRAND;
    window.globalConfig.INSTALLATION_NAME = BRAND;
    window.globalConfig.LOGOUT_REDIRECT_LINK = '/app/login';
  }
  if (window.chatwootConfig) {
    window.chatwootConfig.brandName = BRAND;
  }

  // Update document title - replace any occurrence of Chatwoot
  const updateTitle = () => {
    if (!document.title) return;
    if (/chatwoot/i.test(document.title)) {
      document.title = document.title.replace(/chatwoot/gi, BRAND);
    }
    // Also fix if title is exactly "Chatwoot" or empty
    if (document.title.trim() === '' || document.title.trim().toLowerCase() === 'chatwoot') {
      document.title = BRAND;
    }
  };
  updateTitle();
  // Watch title element for changes
  const titleEl = document.querySelector('title');
  if (titleEl) {
    new MutationObserver(updateTitle).observe(titleEl, { childList: true, characterData: true, subtree: true });
  }
  // Also watch head for title replacement
  new MutationObserver(updateTitle).observe(document.head, { childList: true, subtree: true });
  // Fallback: check periodically for first 10 seconds
  let titleChecks = 0;
  const titleInterval = setInterval(() => {
    updateTitle();
    if (++titleChecks >= 20) clearInterval(titleInterval);
  }, 500);

  // Update theme-color meta
  document.querySelectorAll('meta[name="theme-color"]').forEach(m => {
    m.setAttribute('content', THEME_COLOR);
  });

  // Update favicons persistently (Chatwoot resets them on navigation)
  const SZ_FAVICON = '/static/img/sz-favicon.svg';
  const forceFavicon = () => {
    document.querySelectorAll('link[rel*="icon"]').forEach(link => {
      if (!link.href.endsWith('sz-favicon.svg')) {
        link.href = SZ_FAVICON;
      }
    });
  };
  forceFavicon();
  // Watch <head> for favicon changes and override them
  new MutationObserver(forceFavicon).observe(document.head, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['href'],
  });

  // Replace logo images when they appear
  const replaceLogos = () => {
    const isDark = document.documentElement.classList.contains('dark');
    const logo = isDark ? LOGO_DARK : LOGO_LIGHT;

    document.querySelectorAll('img[src*="brand-assets/logo"]').forEach(img => {
      if (img.src.includes('dark')) {
        img.src = LOGO_DARK;
      } else {
        img.src = logo;
      }
    });
  };

  // Observe for logo images
  new MutationObserver(replaceLogos).observe(document.body, {
    childList: true, subtree: true,
  });

  // Replace Chatwoot text in visible DOM
  const replaceBrandText = () => {
    const walker = document.createTreeWalker(
      document.body, NodeFilter.SHOW_TEXT, null
    );
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.textContent && /chatwoot/i.test(node.textContent)) {
        node.textContent = node.textContent.replace(/Chatwoot/g, BRAND).replace(/chatwoot/g, 'solarz');
      }
    }
  };

  // Run once after DOM settles
  setTimeout(replaceBrandText, 2000);


  // ─────────────────────────────────────────────────────────────────────
  // Call bubble v5 — Figma redesign (atendida / não atendida + transcrição)
  // ─────────────────────────────────────────────────────────────────────
  // Marker format produced by twilio-voice-v2:
  //   📞 LIGACAO_VOZ|status:<s>|duration:<sec>|agent:<name>[|call_sid:<sid>]
  // States rendered:
  //   - Atendida (duration > 0): "MM:SS" + agent pill (right of title)
  //   - Não atendida: "Não atendida" label, no agent pill
  //   - +Transcrição pill (right of MM:SS) when twilio-voice has segments stored
  //     (async fetched via /twilio-voice/api/call-has-transcription/<sid>)
  // Date row pulled from the Chatwoot bubble's own timestamp (text-xs) so we
  // honour the user's locale formatting and stay consistent with siblings.

  var SZ_CALL_TRANSCRIPTION_URL = '/twilio-voice/api/call-has-transcription/';
  var SZ_CALL_LOOKUP_URL = '/twilio-voice/api/lookup-call';
  var _szTxnCheckCache = Object.create(null); // call_sid → boolean (mem cache)
  var _szLookupCache = Object.create(null);   // "acc|agent|dur" → call_sid

  // ─────────────────────────────────────────────────────────────────────
  // Deep-link modal: clicking the "Transcrição" pill opens the chamadas
  // page in a full-screen iframe overlay, with ?open_call_sid&modal_only
  // params so chamadas.html auto-opens the recording modal and hides its
  // own dashboard chrome. We listen for postMessage events from the iframe
  // so it can ask us to tear down (close button, no recording found, etc.).
  // ─────────────────────────────────────────────────────────────────────
  function _szGetCwSession() {
    try {
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var c = cookies[i].trim();
        if (c.indexOf('cw_d_session_info=') === 0) {
          var val = decodeURIComponent(c.substring('cw_d_session_info='.length));
          return JSON.parse(val);
        }
      }
    } catch (e) {}
    return null;
  }

  function _szGetAccountId() {
    var m = window.location.pathname.match(/\/accounts\/(\d+)\//);
    if (m) return m[1];
    try {
      var appEl = document.getElementById('app');
      if (appEl && appEl.__vue_app__) {
        var st = appEl.__vue_app__.config.globalProperties.$store;
        if (st && st.getters && st.getters.getCurrentAccountId) {
          return String(st.getters.getCurrentAccountId);
        }
      }
    } catch (e) {}
    return '';
  }

  function _szGetAgentName() {
    try {
      var appEl = document.getElementById('app');
      if (appEl && appEl.__vue_app__) {
        var u = appEl.__vue_app__.config.globalProperties.$store.getters.getCurrentUser;
        if (u) return u.available_name || u.name || '';
      }
    } catch (e) {}
    return '';
  }

  // Prefetched iframe — populated on bubble hover so the modal can open
  // instantly on click instead of waiting ~500ms-1s for chamadas.html to
  // load + the recording API to respond.
  var _szPrep = null; // { callSid, iframe, ready: bool }

  function _szBuildModalSrc(callSid) {
    var acct = _szGetAccountId();
    if (!acct) return '';
    var sess = _szGetCwSession() || {};
    var agent = _szGetAgentName();
    var isDark = document.body.classList.contains('dark')
      || document.documentElement.classList.contains('dark');
    var dark = isDark ? '1' : '0';
    var src = '/twilio-voice/chamadas?account_id=' + encodeURIComponent(acct)
      + '&instance=brasa&dark=' + dark
      + '&open_call_sid=' + encodeURIComponent(callSid)
      + '&modal_only=1';
    if (agent) src += '&agent=' + encodeURIComponent(agent);
    if (sess['access-token']) {
      src += '&token=' + encodeURIComponent(sess['access-token'])
        + '&cw_token=' + encodeURIComponent(sess['access-token']);
    }
    if (sess.client) src += '&cw_client=' + encodeURIComponent(sess.client);
    if (sess.uid) src += '&cw_uid=' + encodeURIComponent(sess.uid);
    return src;
  }

  function _szTeardownCallModal() {
    var ov = document.getElementById('sz-call-modal-overlay');
    if (ov) ov.remove();
    var fr = document.getElementById('sz-call-modal-iframe');
    if (fr) fr.remove();
    document.body.style.overflow = '';
  }

  function _szDiscardPrep() {
    if (_szPrep && _szPrep.iframe && _szPrep.iframe.parentNode) {
      _szPrep.iframe.remove();
    }
    _szPrep = null;
  }

  function _szPrepareCallModal(callSid) {
    if (!callSid) return;
    if (_szPrep && _szPrep.callSid === callSid) return;
    if (document.getElementById('sz-call-modal-iframe')) return; // already open
    _szDiscardPrep();
    var src = _szBuildModalSrc(callSid);
    if (!src) return;
    var iframe = document.createElement('iframe');
    iframe.className = 'sz-chamadas-iframe';
    iframe.src = src;
    // Off-screen but full-size so the modal centers correctly when promoted.
    iframe.style.cssText = 'position:fixed;left:-99999px;top:-99999px;width:100vw;height:100vh;border:0;background:transparent;color-scheme:dark;visibility:hidden;pointer-events:none;';
    iframe.setAttribute('allow', 'autoplay');
    iframe.setAttribute('data-sz-prep-call-sid', callSid);
    document.body.appendChild(iframe);
    _szPrep = { callSid: callSid, iframe: iframe, ready: false };
  }

  function _szOpenCallModal(callSid) {
    if (!callSid) return;
    if (document.getElementById('sz-call-modal-iframe')) return; // already open

    var iframe;
    var ready = false;
    if (_szPrep && _szPrep.callSid === callSid) {
      // Promote prefetched iframe to foreground.
      iframe = _szPrep.iframe;
      ready = _szPrep.ready;
      _szPrep = null;
      iframe.id = 'sz-call-modal-iframe';
      iframe.removeAttribute('data-sz-prep-call-sid');
      iframe.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;border:0;background:transparent;color-scheme:dark;z-index:100000;visibility:' + (ready ? 'visible' : 'hidden') + ';';
    } else {
      var src = _szBuildModalSrc(callSid);
      if (!src) { console.warn('[sz-call-modal] no account_id resolved'); return; }
      iframe = document.createElement('iframe');
      iframe.id = 'sz-call-modal-iframe';
      iframe.className = 'sz-chamadas-iframe';
      iframe.src = src;
      iframe.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;border:0;background:transparent;color-scheme:dark;z-index:100000;visibility:hidden;';
      iframe.setAttribute('allow', 'autoplay');
      document.body.appendChild(iframe);
    }

    var overlay = document.createElement('div');
    overlay.id = 'sz-call-modal-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:99999;';
    overlay.onclick = function (e) { if (e.target === overlay) _szTeardownCallModal(); };
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
  }

  // Listen for iframe → parent signals (close button / no recording found).
  if (!window._szCallModalListenerInstalled) {
    window._szCallModalListenerInstalled = true;
    window.addEventListener('message', function (ev) {
      var d = ev && ev.data;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'sz-call-modal-ready') {
        // Modal is mounted inside the iframe.
        var fr = document.getElementById('sz-call-modal-iframe');
        if (fr) {
          // active iframe → reveal now
          fr.style.visibility = 'visible';
        } else if (_szPrep && _szPrep.iframe) {
          // prefetched iframe finished loading in background; flag ready so
          // promotion in _szOpenCallModal can flip visibility immediately.
          _szPrep.ready = true;
        }
        return;
      }
      if (d.type === 'sz-call-modal-close' || d.type === 'sz-call-modal-empty') {
        _szTeardownCallModal();
        if (d.type === 'sz-call-modal-empty') {
          try { alert('Transcrição ainda não está disponível para essa ligação.'); } catch (e) {}
        }
      }
    });
    // ESC closes the modal too.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.getElementById('sz-call-modal-iframe')) {
        _szTeardownCallModal();
      }
    });
  }

  function _szResolveAgentAvatar(senderName) {
    if (!senderName) return '';
    try {
      var appEl = document.getElementById('app');
      if (!appEl || !appEl.__vue_app__) return '';
      var st = appEl.__vue_app__.config.globalProperties.$store;
      if (!st) return '';
      var ags = (st.state.agents && st.state.agents.records) || [];
      for (var ai = 0; ai < ags.length; ai++) {
        if (ags[ai].name === senderName || ags[ai].available_name === senderName) {
          return ags[ai].thumbnail || ags[ai].avatar_url || '';
        }
      }
    } catch (e) {}
    return '';
  }

  function _szExtractBubbleDate(bubble) {
    // Chatwoot renders its own timestamp inside the bubble as a small
    // ".text-xs" element. Hoist that text into our card and hide the original
    // so we keep locale formatting (e.g. "12 de fev. às 14:32" in pt-BR).
    // SZ: em nota PRIVADA o container .text-xs (.message-meta) inclui o
    // disclaimer "O cliente não pode ver essa mensagem" ANTES do <time>, o que
    // poluía a data (e truncava). Preferir o <time> puro; fallback ao .text-xs.
    var ts = bubble.querySelector('.message-meta time')
      || bubble.querySelector('time')
      || bubble.querySelector('.text-xs')
      || bubble.querySelector('[class*="text-xs"]');
    if (!ts) return '';
    var txt = (ts.textContent || '').trim();
    if (txt) ts.setAttribute('data-sz-call-hide', '1');
    return txt;
  }

  function _szBuildCard(opts) {
    // SVG copied from Figma — phone outline, currentColor for theme support.
    var phoneSvg = '<svg viewBox="0 0 28 28" width="28" height="28" fill="currentColor" aria-hidden="true">' +
      '<path d="M22.5 17.92c-1.44 0-2.83-.23-4.13-.66a1.14 1.14 0 0 0-1.18.28l-1.83 2.3c-3.3-1.58-6.4-4.55-8.04-7.97l2.27-1.94c.32-.32.41-.78.28-1.19A13.3 13.3 0 0 1 9.21 4.6c0-.63-.53-1.15-1.16-1.15H4.66c-.63 0-1.49.28-1.49 1.15C3.17 14.4 11.6 22.83 21.5 22.83c.83 0 1.16-.73 1.16-1.38v-3.38c0-.63-.53-1.15-1.16-1.15z"/></svg>';
    var iconWrap = '<div class="sz-call-v5-iconwrap">' + phoneSvg + '</div>';

    // Agent pill — always present in the title row when we know the agent.
    // Shows the first name only (to keep the pill short) plus avatar (or
    // initials fallback). Sourced from the LIGACAO_VOZ marker's agent field
    // + a $store.state.agents lookup for the avatar URL.
    var firstName = (opts.senderName || '').trim().split(/\s+/)[0] || '';
    var pillAgent = '';
    if (firstName) {
      var avatarHtml = opts.senderAvatar
        ? '<img src="' + opts.senderAvatar + '" alt="" />'
        : '<span class="sz-call-v5-pill__initials">' + (opts.senderInitials || firstName.charAt(0).toUpperCase()) + '</span>';
      pillAgent = '<span class="sz-call-v5-pill sz-call-v5-pill--agent" title="' + (opts.senderName || '') + '">' +
        '<span class="sz-call-v5-pill__avatar">' + avatarHtml + '</span>' +
        '<span class="sz-call-v5-pill__label">' + firstName + '</span>' +
        '</span>';
    }

    // Transcrição pill — rendered as a button so it's clickable when visible.
    // CSS keeps it `display:none` unless (a) data-sz-has-transcription="1"
    // AND (b) the host card is being hovered. The hover requirement gives a
    // calmer default look — pill only surfaces when the agent is actually
    // pointing at the card. Click opens the chamadas iframe modal.
    var pillTxn = '<button type="button" class="sz-call-v5-pill sz-call-v5-pill--txn"' +
      ' data-sz-call-sid="' + (opts.callSid || '') + '"' +
      ' title="Abrir transcrição">' +
      '<span class="sz-call-v5-pill__label">Transcrição</span>' +
      '</button>';

    var statusText = opts.isAnswered ? opts.durStr : opts.statusLabel;

    return '' +
      '<div class="sz-call-v5__inner">' +
        '<div class="sz-call-v5__main">' +
          iconWrap +
          '<div class="sz-call-v5__text">' +
            '<div class="sz-call-v5__row sz-call-v5__row--title">' +
              '<span class="sz-call-v5__title">Ligação de voz</span>' +
              pillAgent +
            '</div>' +
            '<div class="sz-call-v5__row sz-call-v5__row--status">' +
              '<span class="sz-call-v5__status">' + statusText + '</span>' +
              pillTxn +
            '</div>' +
            (opts.dateText
              ? '<div class="sz-call-v5__date">' + opts.dateText + '</div>'
              : '') +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function _szApplyTranscriptionResult(card, callSid, hasTrans) {
    if (callSid) {
      card.setAttribute('data-sz-call-sid', callSid);
      var pill = card.querySelector('.sz-call-v5-pill--txn');
      if (pill) pill.setAttribute('data-sz-call-sid', callSid);
    }
    if (hasTrans) card.setAttribute('data-sz-has-transcription', '1');
  }

  function _szMaybeFetchTranscription(card, callSid, lookupCtx) {
    // Path A: marker already has call_sid (post-2026-05-18 calls) — just check
    // the transcription status directly.
    if (callSid) {
      if (_szTxnCheckCache[callSid] === true) { _szApplyTranscriptionResult(card, callSid, true); return; }
      if (_szTxnCheckCache[callSid] === false) return;
      try {
        fetch(SZ_CALL_TRANSCRIPTION_URL + encodeURIComponent(callSid), { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            var has = !!(d && d.has_transcription);
            _szTxnCheckCache[callSid] = has;
            _szApplyTranscriptionResult(card, callSid, has);
          })
          .catch(function () { /* swallow */ });
      } catch (e) {}
      return;
    }
    // Path B: legacy marker (no call_sid). Look it up by account + agent +
    // duration. The lookup endpoint already includes has_transcription so we
    // skip the second round-trip.
    if (!lookupCtx || !lookupCtx.accountId || !lookupCtx.agent || lookupCtx.duration == null) return;
    var cacheKey = lookupCtx.accountId + '|' + lookupCtx.agent + '|' + lookupCtx.duration;
    var cached = _szLookupCache[cacheKey];
    if (cached === false) return; // tried, nothing matched
    if (cached) { /* a previous call returned the same sid */
      _szApplyTranscriptionResult(card, cached.callSid, cached.hasTrans);
      return;
    }
    try {
      var url = SZ_CALL_LOOKUP_URL
        + '?account_id=' + encodeURIComponent(lookupCtx.accountId)
        + '&agent=' + encodeURIComponent(lookupCtx.agent)
        + '&duration=' + encodeURIComponent(lookupCtx.duration);
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !d.call_sid) { _szLookupCache[cacheKey] = false; return; }
          var hit = { callSid: d.call_sid, hasTrans: !!d.has_transcription };
          _szLookupCache[cacheKey] = hit;
          _szTxnCheckCache[d.call_sid] = hit.hasTrans;
          _szApplyTranscriptionResult(card, hit.callSid, hit.hasTrans);
        })
        .catch(function () { /* swallow */ });
    } catch (e) {}
  }

  function szRenderCallBubbles() {
    try {
      var allBubbles = document.querySelectorAll('[data-bubble-name="text"]:not(.sz-call-done)');
      for (var i = 0; i < allBubbles.length; i++) {
        var bubble = allBubbles[i];
        var textContent = bubble.textContent || '';
        if (textContent.indexOf('LIGACAO_VOZ|') === -1) continue;
        bubble.classList.add('sz-call-done');

        var markerMatch = textContent.match(/LIGACAO_VOZ\|([^\n]+)/);
        if (!markerMatch) continue;
        var parts = markerMatch[1].split('|');
        var data = {};
        for (var p = 0; p < parts.length; p++) {
          var kv = parts[p].split(':');
          if (kv.length >= 2) data[kv[0].trim()] = kv.slice(1).join(':').trim();
        }

        var status = data.status || 'completed';
        var duration = parseInt(data.duration || '0', 10);
        var callSid = data.call_sid || '';
        var senderName = data.agent || '';

        var durStr = '';
        if (duration > 0) {
          var mm = Math.floor(duration / 60);
          var ss = duration % 60;
          durStr = (mm < 10 ? '0' : '') + mm + ':' + (ss < 10 ? '0' : '') + ss;
        }
        var isAnswered = status === 'completed' && duration > 0;
        var statusLabel = isAnswered
          ? 'Atendida'
          : ({
              completed: 'Finalizada',
              'no-answer': 'Não atendida',
              busy: 'Ocupado',
              failed: 'Falhou',
              canceled: 'Cancelada',
            }[status] || status);
        var statusCls = isAnswered ? 'answered' : 'missed';

        // Find the prose element to hide (Chatwoot's own rendered text).
        var proseEl = bubble.querySelector('span.prose-bubble') || bubble.querySelector('.prose');
        if (!proseEl) {
          var allP = bubble.querySelectorAll('p');
          for (var pp = 0; pp < allP.length; pp++) {
            if ((allP[pp].textContent || '').indexOf('LIGACAO_VOZ|') !== -1) {
              proseEl = allP[pp].parentElement;
              break;
            }
          }
        }
        if (!proseEl) continue;
        proseEl.style.display = 'none';

        // Remove any Vue-injected sender badge (the v4 design rendered it
        // outside the card; v5 inlines it as a pill).
        var existingBadge = bubble.querySelector('.sz-sender-badge');
        if (existingBadge) existingBadge.remove();

        var dateText = _szExtractBubbleDate(bubble);
        var senderAvatar = _szResolveAgentAvatar(senderName);
        var senderInitials = senderName
          .split(' ')
          .map(function (w) { return w.charAt(0) || ''; })
          .join('')
          .substring(0, 2)
          .toUpperCase();

        var card = document.createElement('div');
        card.className = 'sz-call-v5 sz-call-v5--' + statusCls;
        if (callSid) card.setAttribute('data-sz-call-sid', callSid);
        card.innerHTML = _szBuildCard({
          isAnswered: isAnswered,
          durStr: durStr,
          statusLabel: statusLabel,
          dateText: dateText,
          callSid: callSid,
          senderName: senderName,
          senderAvatar: senderAvatar,
          senderInitials: senderInitials,
        });

        // Insert at the top of the bubble's flex container so the card replaces
        // the original text visually but the bubble retains its message classes
        // (reactions, timestamps, etc. still work even when hidden).
        var flexContainer = proseEl.parentElement;
        if (flexContainer) flexContainer.insertBefore(card, flexContainer.firstChild);
        else bubble.insertBefore(card, bubble.firstChild);

        bubble.classList.add('sz-call-bubble-active');
        if (isAnswered) bubble.classList.add('sz-call-bubble-answered');
        else bubble.classList.add('sz-call-bubble-missed');

        // Async check for transcription — only relevant for answered calls.
        // When marker has no call_sid (legacy bubbles pre-2026-05-18), fall
        // back to the lookup endpoint using account/agent/duration.
        if (isAnswered) {
          var ctx = null;
          if (!callSid && senderName) {
            ctx = {
              accountId: _szGetAccountId(),
              agent: senderName,
              duration: duration,
            };
          }
          _szMaybeFetchTranscription(card, callSid, ctx);
        }
      }
    } catch (e) {}
  }

  // Run call bubble rendering on DOM changes
  new MutationObserver(szRenderCallBubbles).observe(document.body, { childList: true, subtree: true });

  // Delegated click handler — opens the chamadas modal iframe when the
  // transcription pill is clicked. Capture phase + stopPropagation so the
  // surrounding Chatwoot bubble doesn't also handle the event (e.g. opening
  // its own menu).
  if (!window._szCallPillClickInstalled) {
    window._szCallPillClickInstalled = true;
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      var pill = t.closest('.sz-call-v5-pill--txn');
      if (!pill) return;
      var sid = pill.getAttribute('data-sz-call-sid');
      if (!sid) return;
      ev.preventDefault();
      ev.stopPropagation();
      _szOpenCallModal(sid);
    }, true);

    // Prefetch the modal iframe when the agent points at a card that has
    // transcription. By the time they actually click the pill, chamadas.html
    // is already loaded and the modal is mounted — promotion is instant.
    document.addEventListener('mouseover', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      var card = t.closest('.sz-call-v5[data-sz-has-transcription="1"]');
      if (!card) return;
      var sid = card.getAttribute('data-sz-call-sid');
      if (!sid) return;
      // idempotent — _szPrepareCallModal early-returns when already prepping
      // this sid or when a modal is already open
      _szPrepareCallModal(sid);
    });
  }






  // --- Fix label dot colors in conversation list ---
  function fixLabelDotColors() {
    try {
      var labels = document.querySelectorAll('.conversation .label');
      for (var i = 0; i < labels.length; i++) {
        var el = labels[i];
        if (el.getAttribute('data-sz-dot') === '1') continue;
        var bc = el.style.borderColor;
        if (bc) {
          var m = bc.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
          if (m) {
            el.style.setProperty('--label-color', 'rgb(' + m[1] + ',' + m[2] + ',' + m[3] + ')');
            el.style.background = 'rgba(' + m[1] + ',' + m[2] + ',' + m[3] + ',0.15)';
            el.style.color = 'rgb(' + m[1] + ',' + m[2] + ',' + m[3] + ')';
          }
        }
        el.setAttribute('data-sz-dot', '1');
      }
    } catch (e) {}
  }

  // --- Inject stacked agent avatars in conversation list ---
  function injectStackedAvatars() {
    try {
      var spans = document.querySelectorAll('.conversation [data-test-id="assignee-badge"]');
      if (!spans.length) spans = document.querySelectorAll('.conversation .flex.items-center.flex-shrink-0.gap-2 > [class*="relative"]');
      // Fallback: look for avatar elements in conversation cards
    } catch (e) {}
  }

  // Run periodically
  setInterval(function() {
    fixLabelDotColors();
  }, 500);

  // Also run on DOM changes
  var szListObserver = new MutationObserver(function() {
    fixLabelDotColors();
  });
  var listEl = document.querySelector('.conversations-list') || document.querySelector('[class*="conversations"]');
  if (listEl) {
    szListObserver.observe(listEl, { childList: true, subtree: true });
  }

}
